import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowLeft, CheckCircle2, Globe } from 'lucide-react';

// ✅ IMPORT AUTH CONTEXTS
import { useAuth } from "../../context/studentAuthContext";
import { useTutorAuth } from "../../context/tutorAuthContext";

const RoleSelection = () => {
  const navigate = useNavigate();

  // ✅ SAFE CONTEXT ACCESS (NO CRASH)
  const auth = useAuth?.();
  const tutorAuth = useTutorAuth?.();

  const student = auth?.student || null;
  const tutor = tutorAuth?.tutor || null;

  const [role, setRole] = useState(null);
  const [language, setLanguage] = useState('English');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const languages = ['English', 'Hausa', 'Yoruba', 'Igbo'];

  // ✅ AUTO REDIRECT (SAFE + CLEAN)
  useEffect(() => {
    if (student) {
      navigate('/enroll', { replace: true });
    } else if (tutor) {
      navigate('/tutor/dashboard', { replace: true });
    }
  }, [student, tutor, navigate]);

  const content = {
    English: {
      back: "Back to Home",
      title: "Select Your Portal",
      desc: "Select the role that best fits your purpose so we can customize your dashboard and experience.",
      instructor: "Register as Instructor",
      learner: "Register as Learner",
      continue: "Continue",
      selectRole: "Please select a role first"
    },
    Hausa: {
      back: "Koma Gida",
      title: "Zaɓi Mashigar Ku",
      desc: "Zaɓi matsayin da ya dace da ku domin mu tsara muku shafin ku.",
      instructor: "Shiga a matsayin Malami",
      learner: "Shiga a matsayin Ɗalibi",
      continue: "Ci gaba",
      selectRole: "Da fatan zaɓi matsayin ku da farko"
    },
    Yoruba: {
      back: "Padà sí Ilé",
      title: "Yan Portal Rẹ",
      desc: "Yan ipa ti o ba idi rẹ mu ki a le sọ oju-iwe rẹ di tirẹ.",
      instructor: "Wọlé gẹgẹbi Olùkọ́",
      learner: "Wọlé gẹgẹbi Akẹ́kọ̀ọ́",
      continue: "Tẹ̀síwájú",
      selectRole: "Jọwọ yan ipa akọkọ"
    },
    Igbo: {
      back: "Laghachi n'Ụlọ",
      title: "Họrọ Portal Gị",
      desc: "Họrọ ọrụ dabara n'ebumnuche gị.",
      instructor: "Banye dịka Onye Nkuzi",
      learner: "Banye dịka Onye Mmụta",
      continue: "Gaba n'ihu",
      selectRole: "Biko họrọ ọrụ gị mbụ"
    }
  };

  const t = content[language];

  // ✅ HANDLE CONTINUE
  const handleContinue = () => {
    if (!role) {
      alert(t.selectRole);
      return;
    }

    setLoading(true);

    setTimeout(() => {
      if (role === 'learner') {
        navigate('/register', { replace: true });
      } else {
        navigate('/tutor/register', { replace: true });
      }
    }, 800);
  };

  return (
    <>
      {/* ✅ LOADING SCREEN */}
      {loading && (
        <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
          <div className="w-20 h-20 border-8 border-t-[#1976D2] border-b-gray-200 rounded-full animate-spin" />
        </div>
      )}

      <div className={`min-h-screen flex flex-col md:flex-row ${loading ? 'pointer-events-none opacity-50' : ''}`}>

        {/* LEFT IMAGE */}
        <div className="hidden md:block w-1/2 p-8">
          <div className="relative h-full rounded-[3rem] overflow-hidden">
            <img
              src="africa-edtech-classroom-tablets.jpg"
              alt="learning"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="w-full md:w-1/2 p-8 md:p-16 flex flex-col justify-center">

          {/* TOP NAV */}
          <div className="flex justify-between mb-10">
            <button onClick={() => navigate('/')} className="flex items-center text-sm font-semibold">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.back}
            </button>

            {/* LANGUAGE */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 shadow rounded-full"
              >
                <Globe className="w-4 h-4" />
                {language}
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 bg-white shadow rounded-xl z-10">
                  {languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setLanguage(lang);
                        setDropdownOpen(false);
                      }}
                      className="block w-full px-4 py-2 text-left hover:bg-gray-100"
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* TITLE */}
          <h1 className="text-3xl font-bold mb-4">{t.title}</h1>
          <p className="text-gray-600 mb-8">{t.desc}</p>

          {/* ROLE */}
          <div className="grid gap-4 mb-8">
            {[
              { id: 'instructor', label: t.instructor },
              { id: 'learner', label: t.learner }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setRole(item.id)}
                className={`p-5 rounded-xl border transition ${
                  role === item.id
                    ? 'border-blue-500 bg-blue-50 scale-[0.98]'
                    : 'border-gray-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3">
                  {role === item.id && <CheckCircle2 className="text-blue-600" size={18} />}
                  {item.label}
                </div>
              </button>
            ))}
          </div>

          {/* CONTINUE */}
          <button
            onClick={handleContinue}
            disabled={!role}
            className={`py-4 rounded-xl flex justify-center items-center gap-2 font-semibold transition ${
              role
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {t.continue}
            <ChevronRight />
          </button>

        </div>
      </div>
    </>
  );
};

export default RoleSelection;